'use client';

import { useState } from 'react';
import {
  Download,
  Copy,
  QrCode,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import Joyride from 'react-joyride';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { GLASS_CARD } from '@/components/ui/glass';
import { useGenerateQR } from '@/components/hooks/useGenerateQR';
import DisplayQRTourSteps from '@/components/tour/worker/DisplayQRTourSteps';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { getTourStyles } from '@/components/tour/tourStyles';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      delay: i * 0.08,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const DisplayQR = () => {
  const [runTour, setRunTour] = useState(false);
  const isMobile = window.innerWidth < 640;

  const {
    qrUrl,
    qrCatalogoUrl,
    bookingUrl,
    catalogoUrl,
    loading,
    downloadQR,
    copyLink,
  } = useGenerateQR();

  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const handleDownload = async (url: string) => {
    if (!url) return;
    try {
      await downloadQR(url);
    } catch {
      toast.error('No se pudo descargar el QR');
    }
  };

  const handleCopy = async (url: string) => {
    try {
      await copyLink(url);
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  return (
    <motion.div
      className="w-full space-y-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants} className="tour-title">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Web y QR
          </span>
        </div>
      </motion.div>

      {/* Tour */}
      <Joyride
        steps={DisplayQRTourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {loading ? (
        <motion.div
          variants={itemVariants}
          className="flex justify-center items-center h-32"
        >
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ── QR Página web ── */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className={`tour-card-catalogo relative flex flex-col gap-5 rounded-2xl p-6 ${GLASS_CARD} hover:shadow-md transition-shadow duration-300`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 flex items-center justify-center shrink-0">
                <ExternalLink className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                QR Página web
              </h3>
            </div>

            <div className="flex flex-col items-center gap-4 flex-1 justify-center py-2">
              {qrCatalogoUrl ? (
                <>
                  <div className="tour-qr-image-catalogo relative group">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-200 to-gray-100 dark:from-white/10 dark:to-white/5 translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-300" />
                    <div className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl p-4 border border-gray-100 dark:border-white/10">
                      <img
                        src={qrCatalogoUrl}
                        alt="QR Página web"
                        className="w-40 h-40 block"
                      />
                    </div>
                  </div>
                  {catalogoUrl && (
                    <p className="tour-link-catalogo text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-xs text-center">
                      {catalogoUrl}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-40 h-40 rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center bg-gray-50 dark:bg-white/5">
                    <QrCode className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center leading-relaxed max-w-[200px]">
                    Cuando la empresa genere el QR de la página web aparecerá
                    aquí
                  </p>
                </div>
              )}
            </div>

            {qrCatalogoUrl && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleDownload(qrCatalogoUrl)}
                  className="tour-download-catalogo inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  Descargar QR
                </button>
                <button
                  onClick={() => handleCopy(catalogoUrl)}
                  className="tour-copy-catalogo inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <Copy className="w-4 h-4" />
                  Copiar enlace
                </button>
              </div>
            )}
          </motion.div>

          {/* ── QR de reservas ── */}
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className={`tour-card-reservas relative flex flex-col gap-5 rounded-2xl p-6 ${GLASS_CARD} hover:shadow-md transition-shadow duration-300`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 flex items-center justify-center shrink-0">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                QR de reservas
              </h3>
            </div>

            <div className="flex flex-col items-center gap-4 flex-1 justify-center py-2">
              {qrUrl ? (
                <>
                  <div className="tour-qr-image-reservas relative group">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-200 to-gray-100 dark:from-white/10 dark:to-white/5 translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-300" />
                    <div className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl p-4 border border-gray-100 dark:border-white/10">
                      <img
                        src={qrUrl}
                        alt="QR de reservas"
                        className="w-40 h-40 block"
                      />
                    </div>
                  </div>
                  {bookingUrl && (
                    <p className="tour-link-reservas text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-xs text-center">
                      {bookingUrl}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-40 h-40 rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center bg-gray-50 dark:bg-white/5">
                    <QrCode className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center leading-relaxed max-w-[200px]">
                    En cuanto la empresa genere un QR de citas aparecerá aquí
                  </p>
                </div>
              )}
            </div>

            {qrUrl && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleDownload(qrUrl)}
                  className="tour-download-reservas inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  Descargar QR
                </button>
                <button
                  onClick={() => handleCopy(bookingUrl)}
                  className="tour-copy-reservas inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <Copy className="w-4 h-4" />
                  Copiar enlace
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default DisplayQR;